import Header from "../components/Header";
import React, {useState, useEffect} from "react";
import Footer from "./Footer";
import useStateType from "../components/Stores/StateStore";
import useInsuranceType from "../components/Stores/InsuranceStore";
import DisplayMenu from "../components/DisplayMenu";
import useCohortType from "../components/Stores/CohortStore";
import useAgeType from "../components/Stores/AgeStore";
import useYearType from "../components/Stores/YearStore";
import useSexType from "../components/Stores/SexStore";
import CompGraph from "../components/ComparatorComponents/ComparatorGraph";
import ComparatorDisplayText from "../components/ComparatorComponents/ComparatorDisplayText";
import Filters from "../components/Filters";
import ComparatorSelection from "../components/ComparatorComponents/ComparatorSelection";
import MobileFilters from "@/components/MobileFilters";
import {ChevronDown} from "lucide-react";
import TabletFilters from "@/components/TabletFilters";
import DisplayComparatorMenu from "@/components/ComparatorComponents/DisplayComparatorMenu";
import LoadingState from "@/components/LoadingState";

const isPercentMetric = (dv: number) =>
  (dv >= 4 && dv < 54) || (dv >= 66 && dv <= 76) || [78, 79, 80].includes(dv);

const isCostMetric = (dv: number) => dv >= 54 && dv <= 66;
interface ParametersForAPITypes {
  cohort: number;
  age: number;
  sex: number;
  insurance: number;
}

interface ComparatorProps {
  displayValue: number;
  setDisplayValue: (val: number) => void;
  centersCheck: boolean;
  setCentersCheck: (val: boolean) => void;
  clear: boolean;
  setClear: (val: boolean) => void;
}

const Comparator: React.FC<ComparatorProps> = ({
  displayValue,
  setDisplayValue,
  centersCheck,
  setCentersCheck,
  clear,
  setClear,
}): JSX.Element => {
  const [comparator, setComparator] = useState<string>("State");
  const [graphData, setGraphData] = useState<object[]>();
  const [graphText, setGraphText] = useState<string>();
  const stateCompValue = useStateType((s) => s.stateCompValue);
  const insuranceCompValue = useInsuranceType((s) => s.insuranceCompValue);
  const cohortCompValue = useCohortType((s) => s.cohortCompValue);
  const ageCompValue = useAgeType((s) => s.ageCompValue);
  const sexCompValue = useSexType((s) => s.sexCompValue);
  const yearCompValue = useYearType((s) => s.yearCompValue);
  const cohortValue = useCohortType((state) => state.cohortValue);
  const ageValue = useAgeType((state) => state.ageValue);
  const yearValue = useYearType((state) => state.yearValue);
  const sexValue = useSexType((state) => state.sexValue);
  const insuranceValue = useInsuranceType((state) => state.insuranceValue);
  const stateValue = useStateType((state) => state.stateValue);
  const setStateCompValue = useStateType((s) => s.setStateCompValue);
  const [loading, setLoading] = useState<boolean>(true);
  const [decimalPoint, setDecimalPoint] = useState<number>(0);
  const [valueNonsig, setValueNonsig] = useState<boolean>(false);
  const [graphColor, setGraphColor] = useState<string>("#2A424B");
  const [maxValue, setMaxValue] = useState<number>(400);
  const setDisabledCohort = useCohortType((s) => s.setDisabledCohort);
  const setDisabledAge = useAgeType((s) => s.setDisabledAge);
  const setDisabledSex = useSexType((s) => s.setDisabledSex);
  const setDisabledInsurance = useInsuranceType((s) => s.setDisabledInsurance);
  const [insuranceCompDisabled, setInsuranceCompDisabled] =
    useState<boolean>(false);
  const [value1, setValue1] = useState<number>(0);
  const [value2, setValue2] = useState<number>(0);
  const [isFiltersOpen, setIsFiltersOpen] = useState<boolean>(false);
  const [isCompareByOpen, setIsCompareByOpen] = useState<boolean>(false);

  // Helper function to check if two values are selected for comparison
  const hasTwoValuesSelected = (): boolean => {
    switch (comparator) {
      case "State":
        return (
          stateCompValue[0] !== undefined && stateCompValue[1] !== undefined
        );
      case "Cohort":
        return (
          cohortCompValue[0] !== undefined && cohortCompValue[1] !== undefined
        );
      case "Age":
        return ageCompValue[0] !== undefined && ageCompValue[1] !== undefined;
      case "Sex":
        return sexCompValue[0] !== undefined && sexCompValue[1] !== undefined;
      case "Insurance":
        return (
          insuranceCompValue[0] !== undefined &&
          insuranceCompValue[1] !== undefined
        );
      case "Year":
        return yearCompValue[0] !== undefined && yearCompValue[1] !== undefined;
      default:
        return false;
    }
  };

  useEffect(() => {
    if (isFiltersOpen || isCompareByOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }, [isFiltersOpen, isCompareByOpen]);

  let parametersForAPI: ParametersForAPITypes = {
    cohort: -1,
    age: -1,
    sex: -1,
    insurance: -1,
  };

  const fetchDatafromAPI = async () => {
    setLoading(true);
    try {
      const API_URL = import.meta.env.VITE_REACT_APP_API_URI;
      const disableDataResponse = await fetch(
        `${API_URL}/Disable?display=${displayValue}`,
      ).catch((error) => {
        console.log(error);
      });
      const disableDataJson = await disableDataResponse?.json();
      //if any of the values is -1, disables that menu & gives -1 for the next api call
      if (disableDataJson.cohort) {
        setDisabledCohort(true);
      } else {
        setDisabledCohort(false);
        parametersForAPI.cohort = cohortValue;
      }
      if (disableDataJson.age) {
        setDisabledAge(true);
      } else {
        setDisabledAge(false);
        parametersForAPI.age = ageValue;
      }
      if (disableDataJson.sex) {
        setDisabledSex(true);
      } else {
        setDisabledSex(false);
        parametersForAPI.sex = sexValue;
      }
      if (disableDataJson.insurance) {
        setDisabledInsurance(true);
        setInsuranceCompDisabled(true);
      } else {
        setInsuranceCompDisabled(false);
        setDisabledInsurance(false);
        parametersForAPI.insurance = insuranceValue;
      }
    } catch (error) {
      console.error("Error fetching data: ", error);
    }
    try {
      const API_URL = import.meta.env.VITE_REACT_APP_API_URI;
      let compareResponse;

      if (
        comparator === "State" &&
        stateCompValue[0] !== undefined &&
        stateCompValue[1] !== undefined
      ) {
        compareResponse = await fetch(
          `${API_URL}/StateComparator?cohort=${parametersForAPI.cohort}&age=${parametersForAPI.age}&sex=${parametersForAPI.sex}&insurance=${parametersForAPI.insurance}&year=${yearValue}&state1=${stateCompValue[0]}&state2=${stateCompValue[1]}&display=${displayValue}`,
        ).catch((error) => {
          console.error("StateComparator fetch error:", error);
        });
      } else if (
        comparator === "Cohort" &&
        cohortCompValue[0] !== undefined &&
        cohortCompValue[1] !== undefined
      ) {
        if (parametersForAPI.cohort === -1) {
          setComparator("State");
          setStateCompValue(["All", "Alaska"]);
          compareResponse = await fetch(
            `${API_URL}/StateComparator?cohort=${parametersForAPI.cohort}&age=${parametersForAPI.age}&sex=${parametersForAPI.sex}&insurance=${parametersForAPI.insurance}&year=${yearValue}&state1=${stateCompValue[0]}&state2=${stateCompValue[1]}&display=${displayValue}`,
          ).catch((error) => {
            console.error("StateComparator fetch error:", error);
          });
        } else {
          compareResponse = await fetch(
            `${API_URL}/CohortComparator?age=${parametersForAPI.age}&sex=${parametersForAPI.sex}&state=${stateValue}&insurance=${parametersForAPI.insurance}&year=${yearValue}&cohort1=${cohortCompValue[0]}&cohort2=${cohortCompValue[1]}&display=${displayValue}`,
          ).catch((error) => {
            console.error("StateComparator fetch error:", error);
          });
        }
      } else if (
        comparator === "Age" &&
        ageCompValue[0] !== undefined &&
        ageCompValue[1] !== undefined
      ) {
        compareResponse = await fetch(
          `${API_URL}/AgeComparator?cohort=${parametersForAPI.cohort}&sex=${parametersForAPI.sex}&state=${stateValue}&insurance=${parametersForAPI.insurance}&year=${yearValue}&age1=${ageCompValue[0]}&age2=${ageCompValue[1]}&display=${displayValue}`,
        ).catch((error) => {
          console.error("StateComparator fetch error:", error);
        });
      } else if (
        comparator === "Sex" &&
        sexCompValue[0] !== undefined &&
        sexCompValue[1] !== undefined
      ) {
        compareResponse = await fetch(
          `${API_URL}/SexComparator?cohort=${parametersForAPI.cohort}&age=${parametersForAPI.age}&state=${stateValue}&insurance=${parametersForAPI.insurance}&year=${yearValue}&sex1=${sexCompValue[0]}&sex2=${sexCompValue[1]}&display=${displayValue}`,
        ).catch((error) => {
          console.error("StateComparator fetch error:", error);
        });
      } else if (
        comparator === "Insurance" &&
        insuranceCompValue[0] !== undefined &&
        insuranceCompValue[1] !== undefined
      ) {
        if (parametersForAPI.insurance === -1) {
          setComparator("State");
          setStateCompValue(["All", "Alaska"]);
          compareResponse = await fetch(
            `${API_URL}/StateComparator?cohort=${parametersForAPI.cohort}&age=${parametersForAPI.age}&sex=${parametersForAPI.sex}&insurance=${parametersForAPI.insurance}&year=${yearValue}&state1=${stateCompValue[0]}&state2=${stateCompValue[1]}&display=${displayValue}`,
          ).catch((error) => {
            console.error("StateComparator fetch error:", error);
          });
        } else {
          compareResponse = await fetch(
            `${API_URL}/InsuranceComparator?cohort=${parametersForAPI.cohort}&age=${parametersForAPI.age}&sex=${parametersForAPI.sex}&state=${stateValue}&year=${yearValue}&insurance1=${insuranceCompValue[0]}&insurance2=${insuranceCompValue[1]}&display=${displayValue}`,
          ).catch((error) => {
            console.error("InsuranceComparator fetch error:", error);
          });
        }
      } else if (
        comparator === "Year" &&
        yearCompValue[0] !== undefined &&
        yearCompValue[1] !== undefined
      ) {
        compareResponse = await fetch(
          `${API_URL}/YearComparator?cohort=${parametersForAPI.cohort}&age=${parametersForAPI.age}&state=${stateValue}&insurance=${parametersForAPI.insurance}&sex=${parametersForAPI.sex}&year1=${yearCompValue[0]}&year2=${yearCompValue[1]}&display=${displayValue}`,
        ).catch((error) => {
          console.error("StateComparator fetch error:", error);
        });
      }
      const compareResponseJSON = await compareResponse.json();
      let sortedData = [];
      if (comparator === "State") {
        sortedData = [...compareResponseJSON.data].sort((a, b) => {
          if (a.name === "All") return -1;
          if (b.name === "All") return 1;
          return a.name.localeCompare(b.name);
        });
      } else {
        sortedData = [...compareResponseJSON.data].sort(
          (a, b) => Number(a.name) - Number(b.name),
        );
      }

      setGraphData(sortedData);
      if (
        compareResponseJSON.data[0].value === -1 ||
        compareResponseJSON.data[1].value === -1
      ) {
        setValueNonsig(true);
      } else {
        setValue1(compareResponseJSON.data[0].value);
        setValue2(compareResponseJSON.data[1].value);
        setValueNonsig(false);
      }

      setGraphText(compareResponseJSON.text);
      setDecimalPoint(compareResponseJSON.decimal);
      setGraphColor(compareResponseJSON.color);
      setMaxValue(compareResponseJSON.max_value);
      setLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchDatafromAPI();
  }, [
    comparator,
    stateCompValue,
    insuranceCompValue,
    cohortCompValue,
    ageCompValue,
    yearCompValue,
    sexCompValue,
    cohortValue,
    ageValue,
    sexValue,
    yearValue,
    displayValue,
    insuranceValue,
    stateValue,
  ]);

  return (
    <div className="bg-white flex flex-col min-h-screen overflow-visible w-full">
      <Header />
      <div className="flex-grow">
        <div className="hidden xl:block">
          <div className="flex flex-row items-center text-valmidnight text-xl font-normal ml-6">
            <div className="font-bold">Compare By:</div>
            <label className="ml-4">
              <select
                value={comparator}
                className="select select-bordered text-black"
                onChange={(e) => setComparator(String(e.target.value))}>
                <option value={"State"}>State</option>
                <option value={"Cohort"}>Cohort</option>
                <option value={"Age"}>Age</option>
                <option value={"Sex"}>Sex</option>
                <option value={"Insurance"} disabled={insuranceCompDisabled}>
                  Insurance
                </option>
                <option value={"Year"}>Year</option>
              </select>
            </label>
            <p className="pl-[5%] text-[18px] font-semibold">
              This page allows you to compare two categories side-by-side within
              a selected grouping (e.g., Age, State, Cohort). Begin by choosing
              a display variable and selecting two categories. Then refine the
              results using the available filters.
            </p>
          </div>
          <div className="px-2 py-2 flex flex-row gap-2 max-h-[100vh]">
            <div className="flex flex-col w-[20%] gap-2">
              <ComparatorSelection
                displayValue={displayValue}
                comparator={comparator}
              />
              <div className="flex justify-center 2xl:min-w-full items-start max-h-[72%]">
                <DisplayMenu
                  displayValue={displayValue}
                  setCentersCheck={setCentersCheck}
                  centersCheck={centersCheck}
                  setDisplayValue={setDisplayValue}
                  clear={clear}
                  clearClickable={false}
                  setClear={setClear}
                  showCentersCheckbox={false}
                />
              </div>
            </div>
            <div className="flex flex-col w-[100%] justify-start">
              <div>
                <div className="w-[100%] mb-4">
                  <Filters
                    displayValue={displayValue}
                    map={false}
                    comparator={comparator}
                  />
                </div>
                <div className="mb-10">
                  {displayValue !== undefined ? (
                    !hasTwoValuesSelected() ? (
                      <div className="bg-white text-center rounded-2 text-valcornflowerblue text-[22px] p-4 rounded-box min-h-min shadow-md font-medium w-full flex justify-center">
                        Please select any two values to compare
                      </div>
                    ) : loading ? (
                      <LoadingState displayValue={displayValue} />
                    ) : graphText && !valueNonsig && graphColor ? (
                      <ComparatorDisplayText
                        graphColor={graphColor}
                        displayValue={displayValue}
                        comparator={comparator}
                        value1={value1}
                        value2={value2}
                        graphText={graphText}
                        decimalPoint={decimalPoint}
                        costDisplay={isCostMetric(displayValue)}
                        percentDisplay={isPercentMetric(displayValue)}
                      />
                    ) : null
                  ) : null}
                </div>
              </div>
              <div className="flex justify-center items-center max-h-[80%]">
                {displayValue !== undefined &&
                graphData &&
                graphText &&
                !loading ? (
                  <CompGraph
                    displayValue={displayValue}
                    comparator={comparator}
                    graphData={graphData}
                    graphText={graphText}
                    maxValue={maxValue}
                    graphColor={graphColor}
                    valueNonsig={valueNonsig}
                    decimalPoint={decimalPoint}
                    costDisplay={isCostMetric(displayValue)}
                    percentDisplay={isPercentMetric(displayValue)}
                  />
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className="xl:hidden">
          <div className="w-[95%] mx-auto">
            <p className="text-[16px] md:text-[18px] p-3 font-semibold">
              This page allows you to compare two categories side-by-side within
              a selected grouping (e.g., Age, State, Cohort). Begin by choosing
              a display variable and selecting two categories. Then refine the
              results using the available filters.
            </p>
            <div className="md:hidden">
              <MobileFilters
                displayValue={displayValue}
                map={false}
                comparator={comparator}
              />
            </div>
            <div className="hidden md:block xl:hidden">
              <div className="w-full lg:flex lg:flex-2 lg:gap-6">
                <div className="hidden lg:block xl:hidden">
                  <DisplayMenu
                    displayValue={displayValue}
                    setCentersCheck={setCentersCheck}
                    centersCheck={centersCheck}
                    setDisplayValue={setDisplayValue}
                    clear={clear}
                    clearClickable={false}
                    setClear={setClear}
                  />
                </div>
                <div className="">
                  <TabletFilters
                    displayValue={displayValue}
                    map={false}
                    comparator={comparator}
                  />
                  <div className="hidden lg:flex xl:hidden justify-end">
                    <div className="absolute">
                      {/* <select
                      value={comparator}
                      className="my-4 right-4 ps-4 pe-8 py-2.5 border border-[#2A424BC9] rounded-full bg-white appearance-none"
                      onChange={(e) => setComparator(String(e.target.value))}
                    >
                      <option value={"State"}>State</option>
                      <option value={"Cohort"}>Cohort</option>
                      <option value={"Age"}>Age</option>
                      <option value={"Sex"}>Sex</option>
                      <option value={"Insurance"} disabled={insuranceCompDisabled}>
                        Insurance
                      </option>
                      <option value={"Year"}>Year</option>
                    </select> */}
                      <button
                        onClick={() => setIsCompareByOpen(true)}
                        className="my-4 px-6 py-2.5 border border-[#2A424BC9] rounded-full bg-white">
                        <div className="flex flex-wrap items-center gap-2">
                          Compare By
                          <svg
                            className=""
                            xmlns="http://www.w3.org/2000/svg"
                            width="8"
                            height="4"
                            viewBox="0 0 8 4"
                            fill="none">
                            <path
                              d="M4.3141 3.87947C4.23076 3.95665 4.11779 4 4 4C3.88221 4 3.76924 3.95665 3.6859 3.87947L0.129992 0.58247C0.0467538 0.505196 0 0.400449 0 0.291235C0 0.182021 0.0467538 0.0772736 0.129992 0H7.87001C7.95325 0.0772736 8 0.182021 8 0.291235C8 0.400449 7.95325 0.505196 7.87001 0.58247L4.3141 3.87947Z"
                              fill="#2A424B"
                            />
                          </svg>
                        </div>
                        <p className="block text-center text-[12px] pt-1">
                          {comparator}
                        </p>
                      </button>
                    </div>
                  </div>
                  <div className="hidden lg:block xl:hidden">
                    <div className="my-20">
                      {displayValue !== undefined &&
                        graphData &&
                        graphText &&
                        !loading &&
                        hasTwoValuesSelected() && (
                          <CompGraph
                            displayValue={displayValue}
                            comparator={comparator}
                            graphData={graphData}
                            graphText={graphText}
                            maxValue={maxValue}
                            graphColor={graphColor}
                            valueNonsig={valueNonsig}
                            decimalPoint={decimalPoint}
                            costDisplay={isCostMetric(displayValue)}
                            percentDisplay={isPercentMetric(displayValue)}
                          />
                        )}
                    </div>
                    <div className="mb-4">
                      {displayValue !== undefined ? (
                        !hasTwoValuesSelected() ? (
                          <div className="bg-white text-center rounded-2 text-valcornflowerblue text-[22px] p-4 rounded-box min-h-min shadow-md font-medium w-full flex justify-center">
                            Please select any two values to compare
                          </div>
                        ) : loading ? (
                          <LoadingState displayValue={displayValue} />
                        ) : graphText && !valueNonsig && graphColor ? (
                          <ComparatorDisplayText
                            graphColor={graphColor}
                            displayValue={displayValue}
                            comparator={comparator}
                            value1={value1}
                            value2={value2}
                            graphText={graphText}
                            decimalPoint={decimalPoint}
                            costDisplay={isCostMetric(displayValue)}
                            percentDisplay={isPercentMetric(displayValue)}
                          />
                        ) : null
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:hidden flex flex-wrap justify-between mt-4 mb-[140px]">
              <button
                onClick={() => setIsFiltersOpen(!isFiltersOpen)}
                className="flex flex-wrap items-center gap-2 px-4 my-4 py-2.5 border border-[#2A424BC9] rounded-full bg-[#E5DCDD]">
                Display
                <svg
                  className="mt-0.5"
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none">
                  <path
                    d="M3.26953 1.32812C6.41372 0.957352 9.58726 0.957326 12.7314 1.32812C13.8024 1.45475 14.3267 2.78562 13.6436 3.67773L10.9355 7.21094C10.1248 8.26837 9.6827 9.58678 9.68262 10.9463V15.3906C9.68261 15.4945 9.65479 15.5964 9.60352 15.6846C9.55227 15.7725 9.47911 15.8433 9.39258 15.8896C9.30577 15.9361 9.20798 15.9567 9.11133 15.9482C9.01481 15.9398 8.92227 15.9027 8.84375 15.8418L6.53418 14.0547C6.46742 14.0029 6.41265 13.935 6.375 13.8564C6.33734 13.7779 6.31732 13.6908 6.31738 13.6025V10.9453C6.31735 9.58581 5.8752 8.26781 5.06543 7.20996L2.35742 3.67871C1.67338 2.78658 2.19698 1.45476 3.26953 1.32812ZM12.6143 2.44043C9.73921 2.10202 6.83912 2.08085 3.96094 2.37695L3.38477 2.44043C3.32852 2.4473 3.27517 2.47015 3.23047 2.50586C3.18588 2.54154 3.1509 2.58863 3.12988 2.64258C3.10889 2.6966 3.10247 2.75586 3.11035 2.81348C3.1183 2.87109 3.1409 2.9259 3.17578 2.97168V2.97266L5.88379 6.50488C6.84689 7.76143 7.37209 9.32939 7.37207 10.9463V13.3184L7.3916 13.333L8.54688 14.2275L8.62695 14.2891V10.9453C8.62714 9.32874 9.1533 7.76118 10.1162 6.50488L12.8242 2.97266V2.97168C12.8591 2.92591 12.8817 2.87107 12.8896 2.81348C12.8975 2.75587 12.8911 2.69659 12.8701 2.64258C12.8491 2.58864 12.8141 2.54154 12.7695 2.50586C12.7249 2.47018 12.6714 2.44732 12.6152 2.44043H12.6143Z"
                    fill="#2A424B"
                    stroke="white"
                    stroke-width="0.1"
                  />
                </svg>
              </button>
              {/* <select
              value={comparator}
              className="absolute right-4 ps-4 pe-8 py-2.5 border border-[#2A424BC9] rounded-full bg-white appearance-none"
              onChange={(e) => setComparator(String(e.target.value))}
            >
              <option value={"State"}>State</option>
              <option value={"Cohort"}>Cohort</option>
              <option value={"Age"}>Age</option>
              <option value={"Sex"}>Sex</option>
              <option value={"Insurance"} disabled={insuranceCompDisabled}>
                Insurance
              </option>
              <option value={"Year"}>Year</option>
            </select> */}
              <button
                onClick={() => setIsCompareByOpen(true)}
                className="my-4 px-6 py-2.5 border border-[#2A424BC9] rounded-full bg-white">
                <div className="flex flex-wrap items-center gap-2">
                  Compare By
                  <svg
                    className=""
                    xmlns="http://www.w3.org/2000/svg"
                    width="8"
                    height="4"
                    viewBox="0 0 8 4"
                    fill="none">
                    <path
                      d="M4.3141 3.87947C4.23076 3.95665 4.11779 4 4 4C3.88221 4 3.76924 3.95665 3.6859 3.87947L0.129992 0.58247C0.0467538 0.505196 0 0.400449 0 0.291235C0 0.182021 0.0467538 0.0772736 0.129992 0H7.87001C7.95325 0.0772736 8 0.182021 8 0.291235C8 0.400449 7.95325 0.505196 7.87001 0.58247L4.3141 3.87947Z"
                      fill="#2A424B"
                    />
                  </svg>
                </div>
                <p className="block text-center text-[12px] pt-1">
                  {comparator}
                </p>
              </button>
            </div>
            <div className="lg:hidden">
              <div className="my-4">
                {displayValue !== undefined &&
                  graphData &&
                  graphText &&
                  !loading &&
                  hasTwoValuesSelected() && (
                    <CompGraph
                      displayValue={displayValue}
                      comparator={comparator}
                      graphData={graphData}
                      graphText={graphText}
                      maxValue={maxValue}
                      graphColor={graphColor}
                      valueNonsig={valueNonsig}
                      decimalPoint={decimalPoint}
                      costDisplay={isCostMetric(displayValue)}
                      percentDisplay={isPercentMetric(displayValue)}
                    />
                  )}
              </div>
              <div className="mb-4">
                {displayValue !== undefined ? (
                  !hasTwoValuesSelected() ? (
                    <div className="bg-white text-center rounded-2 text-valcornflowerblue text-[22px] p-4 rounded-box min-h-min shadow-md font-medium w-full flex justify-center">
                      Please select any two values to compare
                    </div>
                  ) : loading ? (
                    <LoadingState displayValue={displayValue} />
                  ) : graphText && !valueNonsig && graphColor ? (
                    <ComparatorDisplayText
                      graphColor={graphColor}
                      displayValue={displayValue}
                      comparator={comparator}
                      value1={value1}
                      value2={value2}
                      graphText={graphText}
                      decimalPoint={decimalPoint}
                      costDisplay={isCostMetric(displayValue)}
                      percentDisplay={isPercentMetric(displayValue)}
                    />
                  ) : null
                ) : null}
              </div>
            </div>
          </div>
          <div
            className={`fixed inset-0 z-50 lg:hidden transition-transform duration-700 ease-in-out ${isFiltersOpen ? "translate-x-0" : "-translate-x-full"}`}>
            <DisplayMenu
              displayValue={displayValue}
              setCentersCheck={setCentersCheck}
              centersCheck={centersCheck}
              setDisplayValue={setDisplayValue}
              clear={clear}
              clearClickable={false}
              setClear={setClear}
              setIsFiltersOpen={() => setIsFiltersOpen(false)}
              showCentersCheckbox={false}
            />
          </div>
          <div
            className={`fixed inset-0 z-50 xl:hidden transition-transform duration-700 ease-in-out ${isCompareByOpen ? "-translate-x-0" : "translate-x-full"}`}>
            <DisplayComparatorMenu
              displayValue={displayValue}
              setIsComparatorOpen={() => setIsCompareByOpen(false)}
              comparator={comparator}
              setComparator={setComparator}
              insuranceCompDisabled={insuranceCompDisabled}
            />
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Comparator;
